import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Grid, Icon, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { AGRI } from 'administration/components/user/constants/HabilitationConstants'
import AgriAction from 'agriAdministration/actions/AgriAction'
import ColorfulCard from 'components/card/ColorfulCard'
import { push } from '@lagunovsky/redux-react-router'
import { flattenDeep, groupBy, maxBy, minBy, orderBy } from 'lodash'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getDate, getYear } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { getLabel } from 'utils/StoreUtils'
import { getI18nTitleData, i18nExist, i18nize } from 'utils/StringUtil'
import CampaignAction from '../../../campaign/actions/CampaignAction'
import ProgressCard from '../../../components/card/ProgressCard'
import EventsAction from '../../../events/actions/EventsAction'
import EventPieChart from '../../../events/components/chart/EventPieChart'
import EventsStationTypeBarChart from '../../../events/components/chart/EventsStationTypeBarChart'
import EventsTypeLegendPanel from '../../../events/components/chart/EventsTypeLegendPanel'
import EventsTypeVerticalBarChart from '../../../events/components/chart/EventsTypeVerticalBarChart'
import { EVENT_TYPES } from '../../../events/constants/EventsConstants'
import { PATH_GLOBALEVENTS } from '../../constants/RouteConstants'
import { STATION_NAME_ASSOCIATION } from '../../../station/constants/StationConstants'
import { getSiteUrl } from '../../../utils/mapUtils/SiteTypes'
import { getStations, getStationTypeNameFromTypeCode } from '../../../utils/StationUtils'
import DtoGlobalEvents from '../../dto/DtoGlobalEvents'
import FormGlobalEvents from './FormGlobalEvents'
import GlobalEventsCard from './GlobalEventsCard'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { Folder } from '@mui/icons-material'
import { lightGrey } from 'utils/constants/ColorTheme'
import SimpleTabList from 'components/list/SimpleTabList'
import useListIndexed from 'utils/customHook/useListIndexed'
import SimpleCircularProgressWithLabel from 'components/progress/SimpleCircularProgressWithLabel'
import { Virtuoso } from 'react-virtuoso'
import { getExploitationEventsTypes, getColor } from 'events/utils/EventUtils'
import ExportFileModal from '../../../components/modal/ExportFileModal'
import ExportAction from '../../../export/actions/ExportAction'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import { formatData } from '../../../utils/ExportDataUtil'

const EXPORT_HEADERS = ['stationType', 'code', 'cityCode', 'city', 'station', 'date', 'type', 'comment', 'problem', 'solution']
// modèles de /MODELES/CAMPAGNES/ : onglets de campagne de tous les modules
const CAMPAIGN_MODEL_TYPE = 'campaign'

const LastYearEventsCharts = ({
    filter = {},
    allEvents = [],
}) => {
    const filteredEventsByDate = useMemo(() => {
        if (!filter.startDate && !filter.endDate) {
            return {
                title: moment().year(),
                events: orderBy(allEvents.filter(e => moment(e.date).year() === moment().year()), 'date', 'desc'),
            }
        }

        const filter1 = hasValue(filter.startDate) ? allEvents.filter(e => e.date > filter.startDate) : allEvents
        const specialFilteredEvents = hasValue(filter.endDate) ? filter1.filter(e => e.date < filter.endDate) : filter1
        if (!specialFilteredEvents.length) {
            return { title: '', events: [] }
        }

        const minYear = getYear(minBy(specialFilteredEvents, 'date').date)
        const maxYear = getYear(maxBy(specialFilteredEvents, 'date').date)
        return {
            title: minYear === maxYear ? minYear : `${minYear} - ${maxYear}`,
            events: orderBy(specialFilteredEvents, 'date', 'desc'),
        }
    }, [allEvents, filter.endDate, filter.startDate])

    return !allEvents.length ? (
        <SimpleCircularProgressWithLabel />
    ) : (
        <>
            <Grid sx={{ fontSize: 22, fontWeight: 'bold' }}>{filteredEventsByDate.title}</Grid>
            <Grid size={12}>
                <EventPieChart
                    events={filteredEventsByDate.events}
                    withoutLegend
                />
            </Grid>
            <Grid size={12}>
                <EventsStationTypeBarChart events={filteredEventsByDate.events} />
            </Grid>
        </>
    )
}

LastYearEventsCharts.propTypes = {
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.endDate,
    }),
    allEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const DisplayedEvents = ({
    filteredEvents = [],
    stationsConfig = {},
}) => {
    const {
        stationTypes,
        installationsLight,
        contacts,
        cities,
        citiesIndex,
        sandreCodes,
        variousMateriels,
        exploitationsExportFullData,
    } = useSelector(store => ({
        stationTypes: store.ReferencialReducer.stationTypes,
        installationsLight: store.InstallationReducer.installationsLight,
        contacts: store.ContactReducer.contacts,
        cities: store.CityReducer.cities,
        citiesIndex: store.CityReducer.citiesIndex,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        exploitationsExportFullData: store.AgriReducer.exploitationsExportFullData,
    }))

    const [sortBy, setSortBy] = useState('date')

    const exploitationsIndex = useListIndexed(exploitationsExportFullData, 'idExploitation')

    const dispatch = useDispatch()

    const getGroupMethod = useCallback(() => {
        switch (sortBy) {
            case 'referent':
                return e => hasValue(e.contactCode) ? getLabel(contacts, e.contactCode) : i18n.unknownReferent
            case 'eventType':
                return e => getLabel(i18nize(EVENT_TYPES), e.eventType) || i18n.unknownType
            case 'stationType':
                const selectStationTypes = stationTypes.map(s => ({
                    code: s.code,
                    type: s.name,
                    name: i18n[s.i18n],
                }))
                return e => getLabel(selectStationTypes, e.stationType) || i18n.unknownStation
            default:
                return e => getYear(e.date, i18n.thisYear)
        }
    }, [sortBy, contacts])

    const getObjectInfoExploitationEvent = useCallback((associateType, associateId, exportFormat = false) => {
        switch (associateType) {
            case 'installation':
                const point = installationsLight.find(({ id }) => id === associateId)
                if (point) {
                    const city = point.townCode ? (citiesIndex[point.townCode] || null) : null
                    const boldInfo = `${point.name ? `${point.name}` : ''}${point.code ? ` [${point.code}]` : ''}`
                    const extraInfo = `${city ? `, ${city.labelWithCode}` : ''}${point.parcel && point.section ? `, ${point.parcel} ${point.section}` : ''}`
                    if (exportFormat) {
                        return `${boldInfo}${extraInfo}`
                    }
                    return (
                        <Grid container size={12}>
                            <Grid sx={{ fontWeight: 'bold' }}>{boldInfo}</Grid>
                            {extraInfo}
                        </Grid>
                    )
                }
                return i18n.unknownPoint
            case 'contact':
                const contact = contacts.find(({ id }) => id === associateId)
                if (contact) {
                    if (exportFormat) {
                        return contact.name || ''
                    }
                    return <Grid sx={{ fontWeight: 'bold' }}>{contact.name || ''}</Grid>
                }
                return i18n.unknownContact
            case 'materiel':
                const materiel = variousMateriels.find(({ id }) => id === associateId)
                if (materiel) {
                    if (materiel.pump) {
                        const pumpCategory = sandreCodes.find((c) => c.field === 'MAT.MOBILITE' && c.code === materiel.mobilityCode)
                        const pumpType = materiel.pump.pumpType ? sandreCodes.find((c) => c.field === 'POMPES.TYPE' && c.code === materiel.pump.pumpType) : null
                        const boldInfo = materiel.reference || ''
                        const extraInfo = `${pumpCategory ? `, ${pumpCategory.name}` : ''}${pumpType ? `, ${pumpType.name}` : ''}`
                        if (exportFormat) {
                            return `${boldInfo}${extraInfo}`
                        }
                        return (
                            <Grid container size={12}>
                                <Grid sx={{ fontWeight: 'bold' }}>{boldInfo}</Grid>
                                {extraInfo}
                            </Grid>
                        )
                    }
                    if (materiel.counter) {
                        const boldInfo = materiel.reference || ''
                        const extraInfo = `${materiel.counter.brand ? `, ${materiel.counter.brand}` : ''}${materiel.counter.installationDate ? `, ${getDate(materiel.counter.installationDate)}` : ''}`
                        if (exportFormat) {
                            return `${boldInfo}${extraInfo}`
                        }
                        return (
                            <Grid container size={12}>
                                <Grid sx={{ fontWeight: 'bold' }}>{boldInfo}</Grid>
                                {extraInfo}
                            </Grid>
                        )
                    }
                    if (exportFormat) {
                        return materiel.reference || ''
                    }
                    return <Grid sx={{ fontWeight: 'bold' }}>{materiel.reference || ''}</Grid>
                }
                return i18n.unknownMaterial
            case 'declaration':
                if (exportFormat) {
                    return i18n.declaration
                }
                return <Grid sx={{ fontWeight: 'bold' }}>{i18n.declaration}</Grid>
            default:
                return i18n.unknown
        }
    }, [installationsLight, contacts, variousMateriels, sandreCodes, citiesIndex])

    const getIconExploitationEvent = useCallback((event) => {
        switch (event.associateType) {
            case 'installation':
                return 'room'
            case 'contact':
                return 'person'
            case 'materiel':
                return 'router'
            case 'declaration':
                return 'description'
            default:
                return 'folder'
        }
    }, [])

    const getCardExploitationEvents = useCallback((events, idExploitation) => {
        const exploitation = exploitationsIndex[idExploitation]
        return (
            <Grid size={12} container>
                <Grid size={12} container spacing={2} sx={{ backgroundColor: lightGrey, padding: 1 }}>
                    <Grid>
                        <Folder />
                    </Grid>
                    <Grid>
                        {`${exploitation.codification} - ${exploitation.name} (${events.length} ${i18n[`event${events.length >= 2 ? 's' : ''}`]})`}
                    </Grid>
                </Grid>
                {events.map((e, i) => {
                    const type = (getExploitationEventsTypes().find((t) => t.code === e.eventType) || {}).label
                    return (
                        <ColorfulCard key={i} color={getColor(e.eventType)} onClick={() => dispatch(push(`dossiers/${idExploitation}/events`))}>
                            <Grid container size={12} alignItems={'center'} sx={{ paddingLeft: 1 }} >
                                <Grid container size={2}>
                                    {e.eventDate ? getDate(e.eventDate) : ''}
                                </Grid>
                                <Grid container size={4}>
                                    {getObjectInfoExploitationEvent(e.associateType, e.associateId)}
                                </Grid>
                                <Grid container size={4}>
                                    {e.comment}
                                </Grid>
                                <Grid container size={2} spacing={0.5} alignItems={'center'} sx={{ padding: 0.5 }}>
                                    <Grid size={'auto'}><Icon>{getIconExploitationEvent(e)}</Icon></Grid>
                                    <Grid container size={'grow'}>
                                        <Grid size={12} sx={{ fontWeight: 'bold' }}>{type}</Grid>
                                        <Grid size={12} >{i18nExist(e.associateType) || ''}</Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </ColorfulCard>
                    )
                })}
            </Grid>
        )
    }, [exploitationsIndex, getObjectInfoExploitationEvent, getIconExploitationEvent, dispatch])

    const datas = useMemo(() => {
        if (!filteredEvents.length) {
            return [<Grid key='no-data' sx={{ paddingLeft: 2 }}>{i18n.noDataToDisplay}</Grid>]
        }

        const groups = groupBy(filteredEvents, getGroupMethod())
        return orderBy(Object.keys(groups), key => key, ['desc']).map((key, i) => {
            const groupEvents = groups[key]
            const stationGroup = groupBy(groupEvents, 'code')
            const stations = Object.keys(stationGroup).map(stationId => {
                const stationEvents = orderBy(stationGroup[stationId], 'date', 'desc')
                if (stationId.startsWith('EXP_#_')) {
                    return getCardExploitationEvents(stationEvents, parseInt(stationId.split('_#_')[1]))
                }
                const firstEvent = stationEvents[0]
                const icon = getSiteUrl(STATION_NAME_ASSOCIATION[getStationTypeNameFromTypeCode(firstEvent.stationType)])
                const station = getStations(stationsConfig, firstEvent.stationType).find(s => s.id == stationId) || {}
                const stationCode = station.code ? `[${station.code}] ` : ''
                const stationName = station.name ? `- ${station.name} ` : ''
                return (
                    <Grid key={i} container size={12}>
                        <Grid size={12} container spacing={2} alignItems={'center'} sx={{ backgroundColor: lightGrey, padding: 1 }}>
                            <img src={icon} style={{ width: '25px' }}/>
                            {`${stationCode}${getLabel(cities, station.townCode)} ${stationName}(${stationEvents.length} ${i18n[`event${stationEvents.length >= 2 ? 's' : ''}`]})`}
                        </Grid>
                        {stationEvents.map((e, j) => (
                            <GlobalEventsCard
                                key={j}
                                event={e}
                            />
                        ))}
                    </Grid>
                )
            })
            return (
                <Grid key={i} size={12}>
                    <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
                        <AccordionSummary>
                            {`${key === 'undefined' ? i18n.unavailableDate : key} (${groupEvents.length} ${getI18nTitleData(i18n.event, i18n.events, groupEvents)})`}
                        </AccordionSummary>
                        <AccordionDetails>
                            <Virtuoso
                                style={{ minHeight: '10rem', height: `calc(95px * ${stations.length})`, maxHeight: '500px' }}
                                data={stations}
                                itemContent={(_, s) => s}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            )
        })
    }, [filteredEvents, getGroupMethod, getCardExploitationEvents, stationsConfig, cities])

    return (
        <Grid size={12}>
            <SimpleTabList
                controlTab={sortBy}
                setControlTab={setSortBy}
                tabs={[
                    {
                        constant: 'date',
                        label: i18n.byDate,
                        icon: 'insert_invitation',
                    },
                    {
                        constant: 'referent',
                        label: i18n.byReferent,
                        icon: 'person',
                    },
                    {
                        constant: 'eventType',
                        label: i18n.byEventType,
                        icon: 'donut_small',
                    },
                    {
                        constant: 'stationType',
                        label: i18n.byStationType,
                        icon: 'location_on',
                    },
                ]}
            >
                {() => (
                    <Grid container size={12} spacing={1}>
                        {datas}
                    </Grid>
                )}
            </SimpleTabList>
        </Grid>
    )
}

DisplayedEvents.propTypes = {
    filteredEvents: PropTypes.arrayOf(PropTypes.shape({})),
    stationsConfig: PropTypes.shape({}),
}

const GlobalEventsApp = () => {
    const {
        piezometryAllEvents,
        hydrometryStationAllEvents,
        qualitometryAllEvents,
        installationAllEvents,
        distributionUnitAllEvents,
        exploitationsAllEvents,
        hydrometricStations,
        hydrosIndex,
        pluviometers,
        piezosIndex,
        piezometers,
        piezometersLight,
        piezometerListSpecific,
        qualitometers,
        qualitosIndex,
        qualitometersListSpecific,
        installationsIndex,
        installationsLight,
        cities,
        exploitationsExportFullData,
        accountHabilitations,
        distributionUnits,
        productionUnits,
    } = useSelector(store => ({
        piezometryAllEvents: store.EventsReducer.piezometryAllEvents,
        hydrometryStationAllEvents: store.EventsReducer.hydrometryStationAllEvents,
        qualitometryAllEvents: store.EventsReducer.qualitometryAllEvents,
        installationAllEvents: store.EventsReducer.installationAllEvents,
        distributionUnitAllEvents: store.EventsReducer.distributionUnitAllEvents,
        exploitationsAllEvents: store.AgriReducer.exploitationsAllEvents,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrosIndex: store.HydrometryReducer.hydrosIndex,
        pluviometers: store.PluviometryReducer.pluviometers,
        piezosIndex: store.PiezometryReducer.piezosIndex,
        piezometers: store.PiezometryReducer.piezometers,
        piezometersLight: store.PiezometryReducer.piezometersLight,
        piezometerListSpecific: store.PiezometryReducer.piezometerListSpecific,
        qualitometers: store.QualityReducer.qualitometers,
        qualitosIndex: store.QualityReducer.qualitosIndex,
        qualitometersListSpecific: store.QualityReducer.qualitometersListSpecific,
        installationsLight: store.InstallationReducer.installationsLight,
        installationsIndex: store.InstallationReducer.installationsIndex,
        cities: store.CityReducer.cities,
        exploitationsExportFullData: store.AgriReducer.exploitationsExportFullData,
        accountHabilitations: store.AccountReducer.accountHabilitations,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        productionUnits: store.ProductionUnitReducer.productionUnits,
    }))

    const [filter, setFilter] = useState({})

    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)

    const exploitationsIndex = useListIndexed(exploitationsExportFullData, 'idExploitation')
    const distributionUnitsIndex = useListIndexed(distributionUnits, 'id')

    const dispatch = useDispatch()

    const fetchGlobalEvents = () => {
        dispatch(CampaignAction.fetchAllCampaigns()).then(() =>
            dispatch(EventsAction.loadGlobalEventsApp(() => setDataLoaded(true), p => setProgress(p))),
        )
    }

    useEffect(() => {
        if (accountHabilitations?.some((h) => h.habilitation === AGRI)) {
            dispatch(AgriAction.fetchAllExploitationsEvents()).then(() => {
                dispatch(AgriAction.fetchExploitationsExportFullData()).then(fetchGlobalEvents)
            })
        } else {
            fetchGlobalEvents()
        }
    }, [])

    useTitle(() => [{
        title: i18n.journal,
        href: PATH_GLOBALEVENTS,
    }], [])

    const allEvents = useMemo(() => {
        const stationEvents = [
            ...piezometryAllEvents.filter(({ stationId }) => !!piezosIndex[stationId]),
            ...qualitometryAllEvents.filter(({ stationId }) => !!qualitosIndex[stationId]),
            ...installationAllEvents.filter(({ stationId }) => !!installationsIndex[stationId]),
            ...hydrometryStationAllEvents.filter(({ stationId }) => !!hydrosIndex[stationId]),
            ...distributionUnitAllEvents.filter(({ stationId }) => !!distributionUnitsIndex[stationId]),
        ].map(e => new DtoGlobalEvents(e))

        const exploitationEvents = exploitationsAllEvents
            .filter(({ idExploitation }) => !!exploitationsIndex[idExploitation])
            .map(e => ({ ...e, code: `EXP_#_${e.idExploitation}`, date: e.eventDate }))

        return [...stationEvents, ...exploitationEvents]
    }, [piezometryAllEvents, qualitometryAllEvents, installationAllEvents, hydrometryStationAllEvents, distributionUnitAllEvents, exploitationsAllEvents, piezosIndex, qualitosIndex, installationsIndex, hydrosIndex, distributionUnitsIndex, exploitationsIndex])


    const stationsConfig = useMemo(() => ({
        qualitometers,
        qualitometersListSpecific,
        piezometers,
        piezometersLight,
        piezometerListSpecific,
        hydrometricStations,
        pluviometers,
        productionUnits,
        distributionUnits,
        installationsLight,
    }), [
        qualitometers,
        qualitometersListSpecific,
        piezometers,
        piezometersLight,
        piezometerListSpecific,
        hydrometricStations,
        pluviometers,
        productionUnits,
        distributionUnits,
        installationsLight,
    ])

    const getFilterEvents = useCallback((events) => {
        const {
            type,
            stationCode,
            startDate,
            endDate,
            eventType,
            campaign,
            showTechnicalEvents,
        } = filter

        return events.filter(e =>
            (!type?.length || type.includes(e.stationType)) &&
            (!hasValue(stationCode) || e.stationId === stationCode) &&
            (!hasValue(startDate) || e.date >= startDate) &&
            (!hasValue(endDate) || e.date <= endDate) &&
            (!eventType?.length || eventType.includes(e.eventType)) &&
            (!hasValue(campaign) || e.campaignCode === campaign) &&
            (showTechnicalEvents || e.eventType !== 'T'),
        )
    }, [filter])

    const filteredEvents = useMemo(() => getFilterEvents(allEvents), [getFilterEvents, allEvents])

    const onExport = useCallback((exportType) => {
        const eventTypes = i18nize(EVENT_TYPES)
        const events = filteredEvents
        // TODO Faire popup d'export avec nouveau choix de sort
        // const groups = groupBy(events, getGroupMethod())
        const groups = groupBy(events, e => getYear(e.date, i18n.thisYear))
        const results = orderBy(Object.keys(groups), key => key, ['desc']).map(key => {
            const stationGroup = groupBy(groups[key], 'code')
            return Object.keys(stationGroup).map(stationId => orderBy(stationGroup[stationId], 'date', 'desc'))
        })
        const exportData = flattenDeep(results).map(e => {
            if (e.idExploitation) {
                const exploitation = exploitationsIndex[e.idExploitation]
                const type = (getExploitationEventsTypes().find((t) => t.code === e.eventType) || {}).label
                return {
                    ...e,
                    stationType: i18n.exploitation,
                    code: exploitation.codification,
                    stationCode: { value: exploitation.stationCode, cellType: 'string' },
                    station: exploitation.name,
                    date: getDate(e.eventDate),
                    type: `${type} - ${i18nExist(e.associateType) || ''}`,
                }
            }
            const station = getStations(stationsConfig, e.stationType).find(s => s.id === e.stationId) || { typeName: 'unknownType' }
            return {
                ...e,
                city: getLabel(cities, station.townCode),
                cityCode: { value: station.townCode, cellType: 'string' },
                stationType: i18n[station.typeName],
                code: station.code,
                stationCode: { value: station.stationCode, cellType: 'string' },
                station: station.name,
                date: getDate(e.date),
                type: getLabel(eventTypes, e.eventType) || i18n.unknownType,
            }
        })
        if (!exportData.length) {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
            return
        }
        const dataWithHeaders = [{ ...exportData[0], headers: EXPORT_HEADERS }, ...exportData.slice(1)]
        dispatch(ExportAction.export(formatData(dataWithHeaders, exportType), exportType, i18n.journal))
    }, [filteredEvents, exploitationsIndex, stationsConfig, cities, dispatch])

    const [isExportOpen, setIsExportOpen] = useState(false)
    const [campaignModels, setCampaignModels] = useState([])

    useEffect(() => {
        if (isExportOpen) {
            dispatch(ExportAction.getEnvironmentModelsByType(CAMPAIGN_MODEL_TYPE)).then(res => setCampaignModels(res || []))
        }
    }, [dispatch, isExportOpen])

    useActions(() => ({
        exportList: [{
            onClick: () => setIsExportOpen(true),
            label: i18n.excel,
        }],
    }), [])

    const exportChoices = [
        {
            name: i18n.journal,
            formats: [{
                type: i18n.excelFile,
                callback: () => onExport('xlsx'),
            }, {
                type: i18n.csvFile,
                callback: () => onExport('csv'),
            }],
        },
        ...campaignModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => dispatch(ExportAction.exportCampaignModel({ stationType: CAMPAIGN_MODEL_TYPE }, model)),
            }],
        })),
    ]

    return (
        <Box padding={1}>
            {!dataLoaded ? (
                <ProgressCard progress={progress} />
            ) : (
                <Grid container spacing={1.5} alignItems='flex-start'>
                    <Grid container size={9}>
                        <Card>
                            <EventsTypeVerticalBarChart events={filteredEvents} />
                            <FormGlobalEvents
                                filter={filter}
                                onValidate={setFilter}
                            />
                        </Card>
                        <DisplayedEvents
                            filteredEvents={filteredEvents}
                            stationsConfig={stationsConfig}
                        />
                    </Grid>
                    <Grid container size={3}>
                        <Card>
                            <Grid
                                container
                                size={12}
                                direction='column'
                                alignItems='center'
                                justifyContent='center'
                                spacing={1}
                                sx={{ padding: 1.5, textAlign: 'center' }}
                            >
                                <EventsTypeLegendPanel />
                                {filteredEvents.length > 0 && (
                                    <>
                                        <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>
                                            {i18n.historic}
                                        </Typography>
                                        <Grid size={12}>
                                            <EventPieChart
                                                events={filteredEvents}
                                                withoutLegend
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <EventsStationTypeBarChart events={filteredEvents}/>
                                        </Grid>
                                        <LastYearEventsCharts
                                            allEvents={filteredEvents}
                                            filter={filter}
                                        />
                                    </>
                                )}
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            )}
            {isExportOpen && (
                <ExportFileModal
                    open={isExportOpen}
                    onClose={() => setIsExportOpen(false)}
                    data={exportChoices}
                    closeOnExport
                />
            )}
        </Box>
    )
}

export default GlobalEventsApp